import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { onEvent } from "../lib/realtime";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, StatusPill, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, MapPin, Archive } from "lucide-react";

const STATUSES = ["planning", "active", "paused", "completed"];
const BLANK = { name: "", address: "", client: "", status: "planning", start_date: "", end_date: "", description: "" };

export default function Projects() {
  const { t, tn } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);

  const [emps, setEmps] = useState([]);
  const [teams, setTeams] = useState([]);
  const load = () => api.get("/projects").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); api.get("/config/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);
  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: (f[key] || []).includes(id) ? f[key].filter((x) => x !== id) : [...(f[key] || []), id] }));
  useEffect(() => onEvent((ev) => { if (ev.type === "config.changed" && ev.payload?.entity === "projects") load(); }), []);

  const open = (row) => { if (row) { setEditId(row.id); setForm(row); } else { setEditId(null); setForm(BLANK); } setModal(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/projects/${editId}`, form); else await api.post("/projects", form);
      toast.success(t("save")); setModal(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archive = async (id) => { await api.delete(`/projects/${id}`); load(); };

  return (
    <div>
      <PageHeader title={t("projects")} subtitle={`${rows.length}`}
        action={can("projects.create") && (
          <Button data-testid="add-project-btn" onClick={() => open(null)} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2"><Plus size={16} /> {t("create")}</Button>
        )} />
      {rows.length === 0 ? <Empty /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((p) => (
            <div key={p.id} data-testid={`project-${p.id}`} className="tactical-card p-4 kpi-accent">
              <div className="flex items-start justify-between">
                <h3 className="font-display font-bold text-lg">{p.name}</h3>
                <StatusPill status={p.status} />
              </div>
              <div className="text-sm text-[var(--text-3)] flex items-center gap-1 mt-1"><MapPin size={13} /> {p.address || "—"}</div>
              <div className="text-sm mt-2">{p.client}</div>
              <div className="text-xs text-[var(--text-3)] mt-1">{p.start_date} → {p.end_date}</div>
              <div className="flex gap-3 mt-3 text-sm">
                {can("projects.edit") && <button onClick={() => open(p)} className="text-[var(--blue)]" data-testid={`project-edit-${p.id}`}>{t("edit")}</button>}
                {can("projects.archive") && <button onClick={() => archive(p.id)} className="text-[var(--red)] flex items-center gap-1" data-testid={`project-archive-${p.id}`}><Archive size={13} /> {t("archive")}</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? t("edit") : t("create")} — {t("project")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("project")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="proj-name" />
            <Input placeholder={t("address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="proj-address" />
            <Input placeholder={t("client")} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="proj-client" />
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="proj-start" />
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="proj-end" />
            </div>
            <select className="border rounded-md h-10 px-2 text-sm w-full" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="proj-status">
              {STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
            <Textarea placeholder={t("description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="mt-2 space-y-2">
            <div>
              <div className="text-xs uppercase font-semibold text-[var(--text-3)] mb-1">{t("team")} ({(form.team_ids || []).length})</div>
              <div className="flex flex-wrap gap-1.5" data-testid="project-teams">
                {teams.map((tm) => <button key={tm.id} type="button" data-testid={`project-team-${tm.id}`} onClick={() => toggle("team_ids", tm.id)} className={`px-3 h-8 rounded-full text-xs font-semibold ${(form.team_ids || []).includes(tm.id) ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{tn(tm.name)}</button>)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-[var(--text-3)] mb-1">{t("employees")} ({(form.worker_ids || []).length})</div>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="project-workers">
                {emps.map((e) => <button key={e.id} type="button" data-testid={`project-worker-${e.id}`} onClick={() => toggle("worker_ids", e.id)} className={`px-3 h-8 rounded-full text-xs font-semibold ${(form.worker_ids || []).includes(e.id) ? "bg-[var(--amber)] text-white" : "tactical-card"}`}>{e.name}</button>)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white" data-testid="proj-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
